/**
 * IosInstallGate — on iPhone/iPad, Mark runs only from the Home Screen
 * (Sydney, 2026-10-01). A Safari tab silently failed to save photo captures
 * (PR #200), and a browser tab keeps its own storage and login apart from the
 * installed app, so a capture saved there is invisible to the app the crew
 * actually uses. This replaces the dismissible "You're in Safari" notice.
 *
 * iOS gives a web page no way to install itself: there is no
 * `beforeinstallprompt`, and WebKit has declined `navigator.install`. So the
 * gate can only show the manual steps.
 *
 * One-time migration exception (PR #201 review): a browser that already holds
 * entries the installed app can't see — not yet delivered, or rejected — may
 * set the gate aside ("Finish recovering existing entries") to send, fix or
 * delete them, or the human can explicitly discard them all. The moment this
 * browser holds none, the gate is latched on for good and offers no way past.
 * A browser that never held any is latched on its first check.
 *
 * It is mounted OVER the app, not instead of it: the app keeps running
 * underneath so entries already saved in this browser can finish sending.
 */
import { useEffect, useState } from "preact/hooks";
import { onOutboxChanged } from "../outbox/changes";
import { countRecoverableOutboxRecords, discardRecoverableOutboxRecords } from "../outbox/db";
import { IosInstallSteps } from "./IosInstallSteps";

export type IosBrowser = "safari" | "other";

/** Chrome, Firefox, Edge, the Google app and in-app browsers all identify
 * themselves; anything that doesn't is Safari (or a Safari view). */
export function detectIosBrowser(ua: string = navigator.userAgent): IosBrowser {
  return /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|DuckDuckGo|YaBrowser|Instagram|FBAN|FBAV|Line\/|Snapchat|LinkedInApp|Twitter/.test(ua)
    ? "other"
    : "safari";
}

/** Set once this browser has been seen holding no recoverable entries; from
 * then on the gate has no exit. Lives beside the outbox in the same origin, so
 * clearing one clears the other — and an empty outbox latches it again. */
export const IOS_GATE_ENFORCED_KEY = "mark.ios_install_gate_enforced";

function readEnforced(): boolean {
  try {
    return localStorage.getItem(IOS_GATE_ENFORCED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeEnforced(): void {
  try {
    localStorage.setItem(IOS_GATE_ENFORCED_KEY, "1");
  } catch {
    /* Unwritable storage: the next load re-counts and latches again. */
  }
}

/** Re-count on every outbox change, and on a slow timer as a backstop for
 * writes that don't announce themselves (a local delete, say). */
const RECOUNT_MS = 10_000;

function entries(n: number): string {
  return `${n} ${n === 1 ? "entry" : "entries"}`;
}

export interface IosInstallGateProps {
  /** Test seams: production callers omit these. */
  browser?: IosBrowser;
  countRecoverable?: () => Promise<number>;
  discardRecoverable?: () => Promise<number>;
  pageUrl?: string;
}

export function IosInstallGate(props: IosInstallGateProps) {
  const browser = props.browser ?? detectIosBrowser();
  const countRecoverable = props.countRecoverable ?? countRecoverableOutboxRecords;
  const discardRecoverable = props.discardRecoverable ?? discardRecoverableOutboxRecords;
  const pageUrl = props.pageUrl ?? location.origin;
  const [enforced, setEnforced] = useState(readEnforced);
  // null until the first count lands; until then the gate offers no exit.
  const [recoverable, setRecoverable] = useState<number | null>(null);
  const [hadRecoverable, setHadRecoverable] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (enforced) return;
    let live = true;
    const recount = () => {
      countRecoverable().then(
        (n) => {
          if (!live) return;
          setRecoverable(n);
          if (n > 0) {
            setHadRecoverable(true);
            return;
          }
          writeEnforced();
          setEnforced(true);
          setRecovering(false);
          setConfirmingDiscard(false);
        },
        // Unknown is not "none": no exit is offered, but nothing is latched
        // either, so a store that fails to open once can't forfeit the entries.
        () => {},
      );
    };
    recount();
    const unsubscribe = onOutboxChanged(recount);
    const timer = setInterval(recount, RECOUNT_MS);
    return () => {
      live = false;
      unsubscribe();
      clearInterval(timer);
    };
    // countRecoverable is a test seam; production passes none, so it never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enforced]);

  function copyLink() {
    navigator.clipboard?.writeText(pageUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => { /* The address is on screen as selectable text. */ },
    );
  }

  function discard() {
    setDiscarding(true);
    // The outbox-change signal drives the recount that latches the gate.
    discardRecoverable().finally(() => {
      setDiscarding(false);
      setConfirmingDiscard(false);
    });
  }

  const pending = !enforced && recoverable !== null && recoverable > 0 ? recoverable : 0;

  if (pending > 0 && recovering) {
    return (
      <aside class="ios-install-gate-banner" data-testid="ios-install-gate-banner" role="status">
        <button type="button" onClick={() => setRecovering(false)}>
          {entries(pending)} left to finish · Install Mark
        </button>
      </aside>
    );
  }

  return (
    <div
      class="ios-install-gate"
      data-testid="ios-install-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ios-install-gate-title"
    >
      <div class="ios-install-gate-body">
        <h1 id="ios-install-gate-title">Add Mark to your Home Screen</h1>
        <p>
          On iPhone, Mark only works from your Home Screen. That keeps your recordings and photos
          safe and lets Mark work offline.
        </p>

        {browser === "safari" ? (
          <IosInstallSteps />
        ) : (
          <>
            <p>
              Open this page in <strong>Safari</strong> to add Mark to your Home Screen. Copy the link,
              then paste it into Safari's address bar.
            </p>
            <p class="ios-install-gate-url" data-testid="ios-install-gate-url">{pageUrl}</p>
            <button type="button" class="mark-chip" onClick={copyLink}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </>
        )}

        <p>
          Then open Mark from your Home Screen and sign in. Already added it? Open it from your Home
          Screen instead of {browser === "safari" ? "Safari" : "this browser"}.
        </p>

        {pending > 0 && !confirmingDiscard && (
          <section class="ios-install-gate-recover" data-testid="ios-install-gate-recover">
            <p>
              {entries(pending)} saved in this browser {pending === 1 ? "hasn't" : "haven't"} been sent
              yet, and the Home Screen app can't see {pending === 1 ? "it" : "them"}. Finish{" "}
              {pending === 1 ? "it" : "them"} here first.
            </p>
            <button type="button" class="ios-install-gate-primary" onClick={() => setRecovering(true)}>
              Finish recovering existing entries
            </button>
            <button type="button" class="ios-install-gate-quiet" onClick={() => setConfirmingDiscard(true)}>
              Discard {pending === 1 ? "it" : "them"}
            </button>
          </section>
        )}

        {pending > 0 && confirmingDiscard && (
          <section class="ios-install-gate-recover" data-testid="ios-install-gate-discard-confirm">
            <p>
              Delete {entries(pending)} from this browser? {pending === 1 ? "It hasn't" : "They haven't"} been
              sent, and {pending === 1 ? "it" : "they"} can't be recovered.
            </p>
            <button type="button" class="ios-install-gate-danger" disabled={discarding} onClick={discard}>
              Delete {entries(pending)}
            </button>
            <button type="button" class="ios-install-gate-quiet" disabled={discarding} onClick={() => setConfirmingDiscard(false)}>
              Keep {pending === 1 ? "it" : "them"}
            </button>
          </section>
        )}

        {enforced && hadRecoverable && (
          <p class="ios-install-gate-done" data-testid="ios-install-gate-done" role="status">
            No entries are left in this browser.
          </p>
        )}
      </div>
    </div>
  );
}
